import { useEffect } from 'react';

/**
 * Injects a `<script type="application/ld+json">` into `<head>` for as long
 * as the calling component is mounted, keyed by `id` so a page can hold more
 * than one block (Organization stays static in `index.html`; this is for
 * structured data that depends on a specific route or its data).
 *
 * `data` is passed as `undefined` while its source query is still loading, so
 * the hook has nothing to render yet — a script mounted with stale or partial
 * data would describe content the page hasn't actually shown, which is the
 * mismatch structured-data guidelines exist to catch.
 */
export function useJsonLd(id: string, data: object | undefined) {
  useEffect(() => {
    if (!data) return;

    let script = document.getElementById(id) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);

    return () => {
      document.getElementById(id)?.remove();
    };
  }, [id, data]);
}
